// Coverage.jsx - Asking us to cover a shipment for more than the standard
// amount. Reached from the footer, from the shipping policy, and from us telling
// somebody to come here. It is not linked from the header or the home page on
// purpose: almost nobody needs it, and a form about insurance in front of every
// visitor is a decision they should not have to make.
//
// The page has one job beyond collecting the request, and it is the timing. Cover
// above the standard amount only binds us if it was agreed before the items were
// posted, so that sentence appears three times: at the top, next to the button,
// and on the screen they see after sending it.

import { useState } from "react";
import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema } from "../lib/seo.jsx";
import { useAsync, useForm } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import * as analytics from "../lib/analytics.js";
import { money } from "../lib/format.js";
import { Button, Input, Textarea, Alert, Panel } from "../ui/kit.jsx";
import { Shield, CheckCircle, Phone } from "../ui/icons.jsx";
import { Reveal } from "../ui/motion.jsx";
import { PageHead } from "./parts.jsx";

export default function Coverage() {
  const { data: settings } = useAsync(() => publicApi.settings(), []);
  const standard = settings?.coverage?.standard ?? 1000;
  const maximum = settings?.coverage?.maximum ?? 25000;
  const open = settings?.coverage?.requests !== false;
  const company = settings?.company;

  const [sent, setSent] = useState(null);
  const form = useForm({ orderNumber: "", email: "", requestedValue: "", pieces: "", description: "", website: "" });

  const submit = form.submit(async (values) => {
    const result = await publicApi.requestCoverage(values);
    // The amount asked for, which is the customer's own figure off the form.
    analytics.trackCoverageRequested({ requested: Number(values.requestedValue) || undefined });
    setSent(result);
    return result;
  });

  return (
    <>
      <Seo
        title="Higher value cover"
        path="/extra-coverage"
        description={`Every shipment to us is covered to ${money(standard)} automatically. If you are sending more than that, ask us to cover it first. Approved in advance, up to ${money(maximum)}.`}
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Higher value cover", to: "/extra-coverage" }])} />

      <PageHead
        eyebrow="Shipping"
        title="Sending something worth more?"
        lede={`Every package that comes to us is covered to ${money(standard)}, automatically, with nothing to fill in. If what you are sending is worth more than that, ask us first and we will tell you whether we can cover it.`}
      />

      <div className="shell pb-14 md:pb-24">
        <div className="grid gap-9 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <div className="space-y-4">
            <Panel className="p-6">
              <Shield size={20} className="text-bronze mb-3" />
              <p className="eyebrow">What you already have</p>
              <p className="display display-md text-ink mt-1.5">{money(standard)}</p>
              <p className="hint mt-2 leading-relaxed">
                On every shipment, in both directions, with no form and no conversation. This covers almost
                everything people send us, and if it covers yours there is nothing on this page for you to do.
              </p>
            </Panel>

            <Panel className="p-6">
              <p className="eyebrow">The most we can cover</p>
              <p className="display display-md text-ink mt-1.5">{money(maximum)}</p>
              <p className="hint mt-2 leading-relaxed">
                Agreed case by case, before anything is posted. We have to know that a package of that value is
                coming before we can take responsibility for it, which is the whole reason this form exists.
              </p>
            </Panel>

            <Panel quiet className="p-6">
              <p className="eyebrow">Not sure?</p>
              <p className="text-[15px] text-ink-soft mt-2 leading-relaxed">
                Call us and describe what you have. It takes two minutes and we will tell you honestly whether
                you need this, which most people do not.
              </p>
              <a href="tel:+1-401-315-4653" className="link text-ink font-semibold num mt-3 inline-flex items-center gap-2">
                <Phone size={16} /> {company?.phone || "(401) 315-4653"}
              </a>
            </Panel>
          </div>

          <Reveal>
            {sent ? (
              <Panel className="p-7">
                <CheckCircle size={26} className="text-bronze" />
                <h2 className="display display-md text-ink mt-4">We have it</h2>
                <p className="lede mt-3 text-muted">
                  Your request on {sent.orderNumber} is with a person now. We usually answer the same business day,
                  by email and by text.
                </p>
                <Alert tone="warning" className="mt-5" title="Please do not post anything yet">
                  Cover above {money(sent.standard)} only applies to a shipment we agreed to beforehand. Wait until
                  you have our answer in writing. If you would rather not wait, you can send today under the
                  standard {money(sent.standard)} instead, and nothing about your offer changes either way.
                </Alert>
                <Link to="/track" className="btn btn-quiet mt-5">Track your order</Link>
              </Panel>
            ) : (
              <Panel className="p-6 md:p-7">
                <h2 className="display display-md text-ink">Ask for higher cover</h2>
                <p className="hint mt-2 leading-relaxed">
                  You need your order number for this, which is on every message we have sent you and at the top of
                  your tracking page. If you have not asked for a kit yet,{" "}
                  <Link to="/get-a-kit" className="link">start there</Link> and come back once you have the number.
                </p>

                {!open ? (
                  <Alert tone="warning" className="mt-5" title="Not taking requests at the moment">
                    Call us instead and we will sort it out on the phone. The standard {money(standard)} is unaffected.
                  </Alert>
                ) : (
                  <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input
                        label="Order number"
                        placeholder="IGS-XXXXXX"
                        required
                        className="num"
                        {...form.field("orderNumber")}
                      />
                      <Input
                        label="Email on the order"
                        type="email"
                        required
                        hint="So we know it is you"
                        {...form.field("email")}
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input
                        label="Cover you are asking for"
                        type="number"
                        min={standard + 1}
                        max={maximum}
                        step="500"
                        prefix="$"
                        className="pl-7"
                        required
                        hint={`More than ${money(standard)}, up to ${money(maximum)}`}
                        {...form.field("requestedValue")}
                      />
                      <Input
                        label="How many pieces"
                        type="number"
                        min="1"
                        hint="A rough count is fine"
                        {...form.field("pieces")}
                      />
                    </div>

                    <Textarea
                      label="What are you sending?"
                      rows={5}
                      required
                      hint="Metals, rough weights, and anything unusual. We are deciding what we can take responsibility for, so detail helps us say yes."
                      {...form.field("description")}
                    />

                    {/* Not a real field. Anything typed here is a script. */}
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      className="hidden"
                      aria-hidden="true"
                      {...form.field("website")}
                    />

                    {form.message && <Alert tone={form.message.kind === "success" ? "success" : "error"}>{form.message.text}</Alert>}

                    <Alert tone="warning" title="Send this before you post your items">
                      We can only take responsibility for a package we knew about. A request that arrives after your
                      items are in the post cannot be backdated, and the standard {money(standard)} would apply.
                    </Alert>

                    <Button type="submit" loading={form.busy} className="w-full sm:w-auto">
                      Send the request
                    </Button>

                    <p className="hint">
                      Answered by a person, usually the same business day. Approving is our decision and we do
                      sometimes say no, in which case your shipment still carries the standard {money(standard)}.
                      The full rules are in our{" "}
                      <Link to="/legal/shipping" className="link">shipping, insurance and returns policy</Link>.
                    </p>
                  </form>
                )}
              </Panel>
            )}
          </Reveal>
        </div>
      </div>
    </>
  );
}
